import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FlavorImage from './FlavorImage';
import './ModalProductVideo.scss'; // Import dedicated modal video styles
import './MobileProductVideo.scss'; // Import video container styles
import { useMediaDisplay } from '../context/MediaDisplayContext'; // Import media display context
import { getProductVideoPath } from '../utils/productImages';

/**
 * MobileProductVideo component for rendering 3D product videos on mobile and in modals
 * Uses Intersection Observer to only play videos when in view
 * 
 * @param {Object} props - Component props
 * @param {Object} props.product - Product data
 * @param {Object} props.canonicalProduct - Canonical product data
 * @param {string} props.imagePath - Path to product image
 * @param {boolean} props.inModal - Whether video is shown in modal context
 * @param {boolean} props.directVideo - Whether to render just the video without containers (for modal)
 */
const MobileProductVideo = ({ 
  product, 
  canonicalProduct, 
  imagePath, 
  inModal = false, 
  directVideo = false,
  loading = "eager", // Add loading prop with default
  deferVideoLoading = false // Even more aggressive deferral for expanded card
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(true); // Default to true for directVideo
  const [videoFailed, setVideoFailed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [shouldShowVideo, setShouldShowVideo] = useState(false);
  // For expanded card, start with image first, then transition to video for better performance
  const [videoDeferred, setVideoDeferred] = useState(inModal && (loading === "lazy" || deferVideoLoading));
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoTimeoutRef = useRef(null);
  
  // Get media display state from context (showVideos, toggleMediaDisplay)
  const { showVideos, toggleMediaDisplay } = useMediaDisplay();

  // Check if we're on a mobile device (width < 768px) or in a modal
  useEffect(() => {
    // Using matchMedia for better performance and accuracy
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    
    const handleMediaQueryChange = (event) => {
      setIsMobile(event.matches);
      
      // If in a modal, always show video regardless of screen size
      // Otherwise, only show on mobile
      setShouldShowVideo(inModal || event.matches);
      
      // If switching to desktop and not in modal, ensure videos are paused to save resources
      if (!event.matches && !inModal && videoRef.current) {
        videoRef.current.pause();
      }
    };
    
    // Initial check
    setIsMobile(mediaQuery.matches);
    setShouldShowVideo(inModal || mediaQuery.matches);
    
    // Modern approach using addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaQueryChange);
      return () => mediaQuery.removeEventListener('change', handleMediaQueryChange);
    } 
    // Fallback for older browsers
    else {
      mediaQuery.addListener(handleMediaQueryChange);
      return () => mediaQuery.removeListener(handleMediaQueryChange);
    }
  }, [inModal]);

  // Set up Intersection Observer to detect when the video is in view
  // For expanded card with lazy loading, defer video loading until UI is ready
  useEffect(() => {
    if (videoDeferred) {
      // Clear any existing timeout
      if (videoTimeoutRef.current) {
        clearTimeout(videoTimeoutRef.current);
      }
      
      // Use requestAnimationFrame for smoother UI 
      // This will ensure the UI is rendered first before handling video
      videoTimeoutRef.current = setTimeout(() => {
        // Use requestAnimationFrame to schedule video loading after paint
        requestAnimationFrame(() => {
          setVideoDeferred(false);
          // Start loading the video
          if (videoRef.current) {
            videoRef.current.load();
          }
        });
      }, 50); // Reduced delay to match dropdown speed
      
      return () => {
        if (videoTimeoutRef.current) {
          clearTimeout(videoTimeoutRef.current);
        }
      };
    }
  }, [videoDeferred, deferVideoLoading]);

  useEffect(() => {
    if (!containerRef.current || !shouldShowVideo || videoDeferred) return;

    const options = {
      root: null, // Use the viewport
      rootMargin: '50px', // Load videos slightly before they come into view
      threshold: 0.3 // 30% of the element is visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        setIsInView(entry.isIntersecting);
        
        // Play/pause video based on visibility
        if (videoRef.current) {
          if (entry.isIntersecting) {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video in view, playing:', videoRef.current.src);
            }
            
            // Set playback quality to low on mobile to save bandwidth
            if (videoRef.current.canPlayType) {
              videoRef.current.setAttribute('playsinline', '');
              videoRef.current.setAttribute('preload', 'auto');
            }
            
            // Ensure video plays immediately and doesn't pause on toggle
            videoRef.current.play().catch(err => {
              console.error('Error playing video:', err);
              setVideoFailed(true);
            });
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('Video out of view, pausing:', videoRef.current.src);
            }
            videoRef.current.pause();
          }
        }
      });
    }, options);

    observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [shouldShowVideo]);

  // Only render the video component if it should be shown (mobile or in modal)
  if (!shouldShowVideo) {
    return (
      <FlavorImage 
        src={imagePath} 
        alt={canonicalProduct?.displayName || product.name} 
      />
    );
  }

  // Get the video path using the simple utility function
  const videoPath = getProductVideoPath(canonicalProduct);
  
  // Handle clicking on media to toggle between videos and images
  const handleMediaClick = (e) => {
    // Stop propagation to prevent other click handlers from firing
    e.stopPropagation();
    
    // Preload the next state before switching
    if (showVideos) {
      // If we're about to show images, ensure they're preloaded
      const img = new Image();
      img.src = imagePath;
    } else if (videoRef.current) {
      // If we're about to show videos, preload them
      videoRef.current.load();
    }
    
    // Toggle the global media display state
    toggleMediaDisplay();
    
    // Make sure video plays immediately after toggle (if toggling to video)
    if (videoRef.current && !showVideos) {
      // Use requestAnimationFrame for smoother transitions
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.error('Error playing video after toggle:', err);
          });
        }
      });
    }
  };
  
  // If showing images (not videos), or no video path, or video failed, or video is deferred - show image
  if (!showVideos || !videoPath || videoFailed || videoDeferred) {
    // When in modal, use direct styled image to avoid container issues
    if (inModal) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '160px',
          width: '160px'
        }}>
          <img 
            src={imagePath} 
            alt={canonicalProduct?.displayName || product.name}
            onClick={handleMediaClick}
            // Preload the image for smoother transitions
            onLoad={(e) => e.target.style.opacity = 1}
            style={{ 
              width: '160px', 
              height: '160px', 
              maxWidth: '160px', 
              maxHeight: '160px', 
              objectFit: 'contain',
              borderRadius: '8px',
              padding: 0,
              margin: 0,
              display: 'block',
              boxSizing: 'content-box',
              cursor: 'pointer', // Add pointer cursor to indicate it's clickable
              opacity: 0.99, // Start with almost full opacity for smoother transition
              transition: 'opacity 0.1s ease' // Add a quick fade-in transition
            }}
          />
        </div>
      );
    }
    
    // Otherwise use FlavorImage as normal with click handler
    return (
      <div 
        onClick={handleMediaClick} 
        style={{ 
          cursor: 'pointer',
          position: 'relative',
          transition: 'opacity 0.1s ease'
        }}
      >
        <FlavorImage 
          src={imagePath} 
          alt={canonicalProduct?.displayName || product.name}
          style={{
            opacity: 0.99, // Start with almost full opacity for smoother transition
            transition: 'opacity 0.1s ease' // Add a quick fade-in transition
          }}
        />
      </div>
    );
  }

  // We no longer need pause/play functionality
  const handleTouchStart = () => {
    // No longer pausing video on touch
  };
  
  const handleTouchEnd = () => {
    // No longer resuming video on touch end
  };

  // For direct video rendering in modal (no containers)
  if (directVideo && videoPath) {
    // Return a video element inside a div with in-modal class for CSS targeting
    return (
      <div 
        className="product-hero-image in-modal" 
        onClick={handleMediaClick}
        style={{
          height: '160px',
          width: '160px',
          padding: 0,
          paddingBottom: 0,
          margin: 0,
          cursor: 'pointer', // Add pointer cursor to indicate it's clickable
          transition: 'opacity 0.1s ease',
          opacity: 1,
          background: `url(${imagePath}) center center no-repeat`,
          backgroundSize: 'contain',
          borderRadius: '8px'
        }}
      >
        <video 
          ref={videoRef}
          className="direct-modal-video"
          autoPlay 
          loop 
          muted 
          playsInline
          poster={imagePath}
          onCanPlayThrough={(e) => {
            // When video can play through, ensure it's visible and notify
            e.target.style.opacity = 1;
            setVideoLoaded(true);
          }}
          style={{ 
            width: '160px', 
            height: '160px',
            opacity: 0, // Start hidden, fade in when loaded
            transition: 'opacity 0.3s ease-in', // Smooth transition from image to video
            maxWidth: '160px', 
            maxHeight: '160px', 
            objectFit: 'contain',
            borderRadius: '8px',
            padding: 0,
            margin: 0,
            display: 'block',
            boxSizing: 'content-box',
            border: 'none',
            background: 'transparent'
            // opacity and transition are defined in the first appearance above
          }}
          onError={(e) => {
            console.error('Error loading video:', videoPath, e);
            setVideoFailed(true);
          }}
        >
          <source src={videoPath} type="video/mp4" />
        </video>
      </div>
    );
  }

  return (
    <div 
      className={`product-video-container ${inModal ? 'in-modal' : ''}`} 
      ref={containerRef}
      onClick={handleMediaClick} // Add click handler to toggle video/image mode
      style={{ 
        cursor: 'pointer', 
        position: 'relative',
        transition: 'opacity 0.1s ease',
        background: `url(${imagePath}) center center no-repeat`,
        backgroundSize: 'contain',
        borderRadius: '8px'
      }} // Add pointer cursor and relative positioning
    >
      {isInView ? (
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline
          className="product-video"
          poster={imagePath} // Use the image as poster to improve perceived loading speed
          onError={(e) => {
            console.error('Error loading video:', videoPath, e);
            setVideoFailed(true);
          }}
          onCanPlayThrough={(e) => {
            // When video can play through, ensure it's visible
            e.target.style.opacity = 1;
            // Once video is loaded, make it visible (handles with CSS)
            if (containerRef.current) {
              containerRef.current.classList.add('video-loaded');
            }
          }}
          style={{
            opacity: 0.99, // Initial opacity
            transition: 'opacity 0.1s ease-in' // Slightly different timing function to avoid duplication
          }}
        >
          <source src={videoPath} type="video/mp4" />
        </video>
      ) : null}
      {/* Always include fallback image that will show if video fails or when loading */}
      <FlavorImage 
        src={imagePath} 
        alt={canonicalProduct?.displayName || product.name}
        className="fallback-image"
        style={{
          opacity: 0.99,
          transition: 'opacity 0.1s ease'
        }}
      />
    </div>
  );
};

MobileProductVideo.propTypes = {
  product: PropTypes.object.isRequired,
  canonicalProduct: PropTypes.object,
  imagePath: PropTypes.string,
  inModal: PropTypes.bool,
  directVideo: PropTypes.bool,
  loading: PropTypes.oneOf(['lazy', 'eager']), // Add loading prop type
  deferVideoLoading: PropTypes.bool // Add prop for aggressive deferral
};

export default MobileProductVideo;